<template>
  <aside class="relative flex h-full">
    <!--
      No ground layer here any more. This element used to paint `brand-gradient.jpg` a second
      time, at a second URL, behind the rail — which is where the ground came from before it
      was moved onto `body` in `_brand-ground.scss`. Keeping it would have re-fetched 537 KB
      under a different cache key to draw a slice of an image that is already there, pinned to
      the same viewport box by `background-attachment: fixed`. The rail is glass now
      (`surface-chrome`) and the body ground shows through it.
    -->
    <primary-sidebar
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      @toggle-accounts="toggleAccountModal"
      @key-shortcut-modal="toggleKeyShortcutModal"
      @open-notification-panel="openNotificationPanel"
    />
    <!--
      Below `md` the secondary sidebar is an overlay drawer, not a third column: as an inline
      flex sibling it took its 208px out of the content and left 110px of conversation list on
      a 390px screen. The backdrop is what makes it a drawer rather than a floating panel —
      it dims what is behind, and it is the affordance that says "tap here to go back".
      It renders only where the drawer is detached, so on a desk nothing is over the content.
    -->
    <transition name="sidebar-backdrop">
      <div
        v-if="showSecondarySidebar"
        class="sidebar-backdrop md:hidden"
        aria-hidden="true"
        @click="closeSidebarDrawer"
      />
    </transition>
    <transition name="sidebar-drawer">
      <secondary-sidebar
        v-if="showSecondarySidebar"
        class="sidebar-drawer"
        :class="sidebarClassName"
        :account-id="accountId"
        :inboxes="inboxes"
        :labels="labels"
        :teams="teams"
        :custom-views="customViews"
        :menu-config="activeSecondaryMenu"
        :current-role="currentRole"
        :is-on-chatwoot-cloud="isOnChatwootCloud"
        @add-label="showAddLabelPopup"
        @toggle-accounts="toggleAccountModal"
      />
    </transition>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import adminMixin from '../../mixins/isAdmin';
import { getSidebarItems } from './config/default-sidebar';
import alertMixin from 'shared/mixins/alertMixin';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import {
  hasPressedAltAndCKey,
  hasPressedAltAndRKey,
  hasPressedAltAndSKey,
  hasPressedAltAndVKey,
  hasPressedCommandAndForwardSlash,
  isEscape,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import router from '../../routes';

// The shell breakpoint, in pixels. Below it the three-column shell becomes one column at a
// time and the secondary sidebar is an overlay drawer rather than a column. This is the same
// number as `$screen-md` in `assets/scss/_responsive.scss`, which does the layout half of the
// same decision — the two have to change together, and this is the only place JS reads it.
const SHELL_BREAKPOINT = 768;

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  mixins: [adminMixin, alertMixin, eventListenerMixins],
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      teams: 'teams/getMyTeams',
    }),
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    primaryMenuItems() {
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        const isAvailableForTheUser = menuItem.roles.includes(this.currentRole);

        if (!isAvailableForTheUser) {
          return false;
        }
        if (
          menuItem.alwaysVisibleOnChatwootInstances &&
          !this.isACustomBrandedInstance
        ) {
          return true;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const activePrimaryMenu =
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {};
      return activePrimaryMenu;
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
    // A drawer that stays open over the screen it just navigated to is not a drawer, it is a
    // panel the user has to dismiss twice. Only below `md`, where it is detached — on a desk
    // the sidebar is a column and closing it on every click would be destructive.
    $route() {
      if (this.showSecondarySidebar && this.isDrawerLayout()) {
        this.closeSidebarDrawer();
      }
    },
  },
  mounted() {
    // A drawer is closed until someone opens it. `show_secondary_sidebar` is persisted on the
    // user profile, so an account last used at desk width arrives on a phone with the sidebar
    // "open" — which used to mean a squeezed third column and now would mean a backdrop over
    // the whole screen on first paint. Dashboard.vue's `handleResize` already intends this,
    // but it only fires when the layout type CHANGES, so it does nothing on a cold load.
    if (this.showSecondarySidebar && this.isDrawerLayout()) {
      this.closeSidebarDrawer();
    }
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchCustomViews();
  },

  methods: {
    // The one place that decides whether the sidebar is a column or a drawer, asked of the
    // browser rather than tracked in state, so a rotation or a resize is answered correctly
    // without a listener.
    isDrawerLayout() {
      return window.matchMedia(`(max-width: ${SHELL_BREAKPOINT - 1}px)`)
        .matches;
    },
    // Closes through the toggle the product already owns, so the drawer and the desktop
    // column read from and write to one piece of state, not two.
    closeSidebarDrawer() {
      bus.$emit(BUS_EVENTS.TOGGLE_SIDEMENU);
    },
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleKeyShortcutModal() {
      this.$emit('open-key-shortcut-modal');
    },
    closeKeyShortcutModal() {
      this.$emit('close-key-shortcut-modal');
    },
    handleKeyEvents(e) {
      if (hasPressedCommandAndForwardSlash(e)) {
        this.toggleKeyShortcutModal();
      }
      if (isEscape(e)) {
        this.closeKeyShortcutModal();
        if (this.showSecondarySidebar && this.isDrawerLayout()) {
          this.closeSidebarDrawer();
        }
      }

      if (hasPressedAltAndCKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('home')) {
          router.push({ name: 'home' });
        }
      } else if (hasPressedAltAndVKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('contacts_dashboard')) {
          router.push({ name: 'contacts_dashboard' });
        }
      } else if (hasPressedAltAndRKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('settings_account_reports')) {
          router.push({ name: 'settings_account_reports' });
        }
      } else if (hasPressedAltAndSKey(e)) {
        if (!this.isCurrentRouteSameAsNavigation('agent_list')) {
          router.push({ name: 'agent_list' });
        }
      }
    },
    isCurrentRouteSameAsNavigation(routeName) {
      return this.$route.name === routeName;
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggle-account-modal');
    },
    showAddLabelPopup() {
      this.$emit('show-add-label-popup');
    },
    openNotificationPanel() {
      this.$emit('open-notification-panel');
    },
  },
};
</script>
